import {
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiEye } from "@mdi/js";
import {
  Alert,
  Box,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";
import type {
  RequestAnticipationEvaluationQuotation,
  RequestAnticipationEvaluationQuotationsFilter,
} from "../../../@types/services/RequestAnticipationEvaluationQuotation";
import { listRequestAnticipationServiceQuotations } from "../../../__generated__/orval/endpoints/request-anticipation-service-quotations/request-anticipation-service-quotations";
import type { EntitiesServiceQuotationRequestAnticipationListResponse } from "../../../__generated__/orval/models/entitiesServiceQuotationRequestAnticipationListResponse.zod";
import { ForbiddenError } from "../../../errors/ForbiddenError";
import { useAuth } from "../../../hooks/useAuth";
import { fetchRequestAnticipationEvaluationQuotations } from "../../../services/requestAnticipationEvaluationQuotations";
import { i18nextParseToDate } from "../../../utils/i18nextParseTo";
import { RequestAnticipationFilters } from "./-internal_components/RequestAnticipationFilters";

const optionSchema = z.object({
  label: z.string(),
  value: z.string(),
});

const formSchema = z.object({
  reference_number: z.string().optional(),
  request_type: z.string().or(optionSchema).optional(),
  requested_by: z.array(z.object({ id: z.number() }).passthrough()).optional(),
  status: z.string().or(optionSchema).optional(),
});

type FormData = z.infer<typeof formSchema>;
type RequestType = Extract<
  UnifiedRow["kind"],
  "evaluation" | "service_quotation"
>;
type ServiceRequestAnticipationListItem =
  EntitiesServiceQuotationRequestAnticipationListResponse["results"][number];
type AppliedFilters = RequestAnticipationEvaluationQuotationsFilter & {
  request_type?: RequestType;
};

type UnifiedRow =
  | { kind: "evaluation"; data: RequestAnticipationEvaluationQuotation }
  | { kind: "service_quotation"; data: ServiceRequestAnticipationListItem };

type UnifiedListSlice<T> = {
  results: T[];
  total_items: number;
};

const getRequestTypeValue = (
  value?: string | { label: string; value: string },
): RequestType | undefined => {
  const parsedValue =
    typeof value === "string" ? value || undefined : value?.value;

  if (parsedValue === "evaluation" || parsedValue === "service_quotation") {
    return parsedValue;
  }

  return undefined;
};

const compareUnifiedRowsByUpdatedAtDesc = (a: UnifiedRow, b: UnifiedRow) =>
  new Date(b.data.updated_at).getTime() - new Date(a.data.updated_at).getTime();

interface UnifiedRequestTableRowProps {
  row: UnifiedRow;
  canViewEvaluationDetails: boolean;
  canViewServiceDetails: boolean;
  onViewDetails: (id: RequestAnticipationEvaluationQuotation["id"]) => void;
  onViewServiceDetails: (id: string) => void;
}

const UnifiedRequestTableRow: React.FC<UnifiedRequestTableRowProps> = ({
  row,
  canViewEvaluationDetails,
  canViewServiceDetails,
  onViewDetails,
  onViewServiceDetails,
}) => {
  const { t } = useTranslation();

  const referenceNumber =
    row.kind === "evaluation"
      ? row.data.quotation.reference_number
      : row.data.service_quotation.reference_number;

  const referenceLink =
    row.kind === "evaluation"
      ? `/vbuyer/quotations/${row.data.quotation_id}`
      : `/vbuyer/service_quotations/${row.data.service_quotation_id}`;

  return (
    <TableRow>
      <TableCell>
        {row.kind === "evaluation"
          ? t("common.quotation")
          : t("common.serviceQuotation")}
      </TableCell>
      <TableCell>{row.data.requested_by.full_name_with_login}</TableCell>
      <TableCell>
        <Link
          href={referenceLink}
          rel="noreferrer"
          target="_blank"
          underline="hover"
        >
          {referenceNumber}
        </Link>
      </TableCell>
      <TableCell>{row.data.reason}</TableCell>
      <TableCell>
        <Tooltip title={t(`common.${row.data.status}`)}>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 1,
            }}
          >
            {t(`common.${row.data.status}`)}
          </Box>
        </Tooltip>
      </TableCell>
      <TableCell>
        {t("intlDate", i18nextParseToDate(row.data.created_at))}
      </TableCell>
      <TableCell align="center">
        {row.kind === "evaluation" && canViewEvaluationDetails && (
          <Tooltip title={t("common.details")}>
            <IconButton
              color="primary"
              onClick={() => onViewDetails(row.data.id)}
            >
              <Icon icon={mdiEye} />
            </IconButton>
          </Tooltip>
        )}
        {row.kind === "service_quotation" && canViewServiceDetails && (
          <Tooltip title={t("common.details")}>
            <IconButton
              color="primary"
              onClick={() => onViewServiceDetails(String(row.data.id))}
            >
              <Icon icon={mdiEye} />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};

const RequestAnticipationEvaluationQuotations: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const navigate = Route.useNavigate();

  const canViewEvaluationDetails =
    user?.roles_names.includes(
      "Request Anticipation Evaluation Quotation - View",
    ) || false;
  const canViewServiceDetails =
    user?.roles_names.includes(
      "Request Anticipation Service Quotation - View",
    ) || false;

  const [rows, setRows] = useState<UnifiedRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [tabOpen, setTabOpen] = useState("open");

  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      reference_number: "",
      request_type: "",
      requested_by: [],
      status: "",
    },
    resolver: zodResolver(formSchema),
  });

  const STATUS_OPTIONS = useMemo(
    () => [
      {
        label: t("common.pending"),
        value: "pending",
      },
      {
        label: t("common.approved"),
        value: "approved",
      },
      {
        label: t("common.rejected"),
        value: "rejected",
      },
      {
        label: t("common.automaticallyClosed"),
        value: "automatically_closed",
      },
    ],
    [t],
  );

  const statusOptions = useMemo(
    () => STATUS_OPTIONS.filter((opt) => opt.value !== "pending"),
    [STATUS_OPTIONS],
  );

  const requestTypeOptions = useMemo(
    () => [
      {
        label: t("common.quotation"),
        value: "evaluation",
      },
      {
        label: t("common.serviceQuotation"),
        value: "service_quotation",
      },
    ],
    [t],
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;

    if (appliedFilters.reference_number) count += 1;
    if (appliedFilters.request_type) count += 1;
    if (appliedFilters.requested_by?.length) count += 1;
    if (appliedFilters.status) count += 1;

    return count;
  }, [appliedFilters]);

  const handleFetchRequests = useCallback(
    async (page: number, filters: AppliedFilters): Promise<void> => {
      setLoading(true);
      try {
        const openRequest = tabOpen === "open";
        const commonFilters: RequestAnticipationEvaluationQuotationsFilter = {
          open_request: openRequest,
          reference_number: filters.reference_number,
          requested_by: filters.requested_by,
          status: filters.status,
        };
        const includeEvaluation = filters.request_type !== "service_quotation";
        const includeService = filters.request_type !== "evaluation";
        const requiredItems = page * rowsPerPage;

        const fetchEvaluationRequests = async (): Promise<
          UnifiedListSlice<RequestAnticipationEvaluationQuotation>
        > => {
          const itemsPerPage = Math.min(requiredItems, 100);
          const firstPage = await fetchRequestAnticipationEvaluationQuotations({
            filters: commonFilters,
            items_per_page: itemsPerPage,
            page: 1,
          });

          const totalItems = firstPage.total_items ?? 0;
          const totalToFetch = Math.min(requiredItems, totalItems);
          const totalPagesToFetch = Math.ceil(totalToFetch / itemsPerPage);

          if (totalPagesToFetch <= 1) {
            return {
              results: (firstPage.results ?? []).slice(0, totalToFetch),
              total_items: totalItems,
            };
          }

          const remainingPages = await Promise.all(
            Array.from({ length: totalPagesToFetch - 1 }, (_, index) =>
              fetchRequestAnticipationEvaluationQuotations({
                filters: commonFilters,
                items_per_page: itemsPerPage,
                page: index + 2,
              }),
            ),
          );

          return {
            results: [
              ...(firstPage.results ?? []),
              ...remainingPages.flatMap((response) => response.results ?? []),
            ].slice(0, totalToFetch),
            total_items: totalItems,
          };
        };

        const fetchServiceRequests = async (): Promise<
          UnifiedListSlice<ServiceRequestAnticipationListItem>
        > => {
          const itemsPerPage = Math.min(requiredItems, 1000);
          const firstPage = await listRequestAnticipationServiceQuotations({
            ...commonFilters,
            items_per_page: itemsPerPage,
            page: 1,
          });

          const totalItems = firstPage.total_items ?? 0;
          const totalToFetch = Math.min(requiredItems, totalItems);
          const totalPagesToFetch = Math.ceil(totalToFetch / itemsPerPage);

          if (totalPagesToFetch <= 1) {
            return {
              results: (firstPage.results ?? []).slice(0, totalToFetch),
              total_items: totalItems,
            };
          }

          const remainingPages = await Promise.all(
            Array.from({ length: totalPagesToFetch - 1 }, (_, index) =>
              listRequestAnticipationServiceQuotations({
                ...commonFilters,
                items_per_page: itemsPerPage,
                page: index + 2,
              }),
            ),
          );

          return {
            results: [
              ...(firstPage.results ?? []),
              ...remainingPages.flatMap((response) => response.results ?? []),
            ].slice(0, totalToFetch),
            total_items: totalItems,
          };
        };

        const [evalResp, sqResp] = await Promise.allSettled([
          includeEvaluation
            ? fetchEvaluationRequests()
            : Promise.resolve({ results: [], total_items: 0 }),
          includeService
            ? fetchServiceRequests()
            : Promise.resolve({ results: [], total_items: 0 }),
        ]);

        const evalResults =
          evalResp.status === "fulfilled" ? (evalResp.value.results ?? []) : [];
        const sqResults =
          sqResp.status === "fulfilled" ? (sqResp.value.results ?? []) : [];
        const evalTotal =
          evalResp.status === "fulfilled"
            ? (evalResp.value.total_items ?? 0)
            : 0;
        const sqTotal =
          sqResp.status === "fulfilled" ? (sqResp.value.total_items ?? 0) : 0;

        const merged: UnifiedRow[] = [
          ...evalResults.map((d) => ({ data: d, kind: "evaluation" as const })),
          ...sqResults.map((d) => ({
            data: d,
            kind: "service_quotation" as const,
          })),
        ].sort(compareUnifiedRowsByUpdatedAtDesc);

        const start = (page - 1) * rowsPerPage;

        setRows(merged.slice(start, start + rowsPerPage));
        setTotalRows(evalTotal + sqTotal);

        if (includeEvaluation && evalResp.status === "rejected") {
          enqueueSnackbar(
            t("requestAnticipationEvaluationQuotations.fetchError"),
          );
        }
        if (includeService && sqResp.status === "rejected") {
          enqueueSnackbar(t("requestAnticipationServiceQuotations.fetchError"));
        }
      } finally {
        setLoading(false);
      }
    },
    [rowsPerPage, tabOpen, t],
  );

  const toggleFilterDrawer = useCallback(() => {
    setIsFilterDrawerOpen((prev) => !prev);
  }, []);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    value: number,
  ): void => {
    setCurrentPage(value + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleFilterSubmit: SubmitHandler<FormData> = (data) => {
    const newFilters: AppliedFilters = {
      reference_number: data.reference_number?.trim() || undefined,
      request_type: getRequestTypeValue(data.request_type),
      requested_by: data.requested_by?.map((option) => option.id),
      status:
        typeof data.status === "string"
          ? data.status || undefined
          : data.status?.value,
    };
    setAppliedFilters(newFilters);
    setCurrentPage(1);
    setIsFilterDrawerOpen(false);
  };

  const handleFilterClear = (): void => {
    reset();
    setAppliedFilters({});
    setCurrentPage(1);
    setIsFilterDrawerOpen(false);
  };

  const handleViewDetails = (
    requestId: RequestAnticipationEvaluationQuotation["id"],
  ): void => {
    navigate({
      params: {
        requestAnticipationEvaluationQuotationId: requestId,
      },
      to: "/request_anticipation_evaluation_quotations/$requestAnticipationEvaluationQuotationId",
    });
  };

  const handleViewServiceDetails = (requestId: string): void => {
    navigate({
      params: {
        requestAnticipationServiceQuotationId: requestId,
      },
      to: "/request_anticipation_service_quotations/$requestAnticipationServiceQuotationId",
    });
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ): void => {
    setTabOpen(newValue);
    reset();
    setAppliedFilters({});
    setCurrentPage(1);
  };

  useEffect(() => {
    handleFetchRequests(currentPage, appliedFilters);
  }, [currentPage, appliedFilters, handleFetchRequests]);

  return (
    <PageContainer
      maxWidth="xl"
      title={t("requestAnticipationEvaluationQuotations.title")}
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs onChange={handleTabChange} value={tabOpen}>
          <Tab
            label={t("requestAnticipationEvaluationQuotations.openTab")}
            value="open"
          />
          <Tab
            label={t("requestAnticipationEvaluationQuotations.closedTab")}
            value="closed"
          />
        </Tabs>
      </Box>

      <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
        <Typography
          sx={{
            color: "text.secondary",
          }}
          variant="body2"
        >
          {t("requestAnticipationEvaluationQuotations.info")}
        </Typography>
      </Alert>

      <RequestAnticipationFilters
        activeFiltersCount={activeFiltersCount}
        control={control}
        handleSubmit={handleSubmit}
        isOpen={isFilterDrawerOpen}
        loading={loading}
        onClear={handleFilterClear}
        onSubmit={handleFilterSubmit}
        requestTypeOptions={requestTypeOptions}
        statusOptions={statusOptions}
        tabOpen={tabOpen}
        toggleDrawer={toggleFilterDrawer}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("common.type")}</TableCell>
              <TableCell>{t("common.requestedBy")}</TableCell>
              <TableCell>{t("common.referenceNumber")}</TableCell>
              <TableCell>{t("common.reason")}</TableCell>
              <TableCell>{t("common.status")}</TableCell>
              <TableCell>{t("common.requestedAt")}</TableCell>
              <TableCell align="center">{t("common.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!!loading && (
              <TableRow key="loading-skeleton">
                {[
                  "type",
                  "requested_by",
                  "reference_number",
                  "reason",
                  "status",
                  "requested_at",
                  "actions",
                ].map((columnKey) => (
                  <TableCell key={columnKey}>
                    <Skeleton />
                  </TableCell>
                ))}
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={7}>
                  {t("requestAnticipationEvaluationQuotations.noRequests")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((row) => (
                <UnifiedRequestTableRow
                  canViewEvaluationDetails={canViewEvaluationDetails}
                  canViewServiceDetails={canViewServiceDetails}
                  key={`${row.kind}-${row.data.id}`}
                  onViewDetails={handleViewDetails}
                  onViewServiceDetails={handleViewServiceDetails}
                  row={row}
                />
              ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={totalRows}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                page={currentPage - 1}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                showFirstButton
                showLastButton
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/request_anticipation_evaluation_quotations/",
)({
  beforeLoad({ context }) {
    if (
      context.auth.user?.roles_names.includes(
        "Request Anticipation Evaluation Quotation - View",
      )
    ) {
      return;
    }

    throw new ForbiddenError();
  },
  component: RequestAnticipationEvaluationQuotations,
});
